import { Flex, Group, Stack, UnstyledButton } from '@mantine/core';
import type { LiteSecodaEntityOut } from '@repo/api-codegen';
import MarkdownRenderer from '@repo/common/components/MarkdownRenderer';
import { UserAvatar } from '@repo/common/components/UserAvatar';
import { useNavigate } from '@repo/common/hooks/useNavigate';
import { Text } from '@repo/foundations';
import { isEmpty, isNil, isString } from 'lodash-es';
import { SecodaEntityIcon } from '../../../../components/SecodaEntity';
import { getEntityPathString } from '../../../../lib/utils/entity';
import { buildResourceUrl } from '../../../../utils/navigationUtils';
import { formatDateTimeShort } from '../../../../utils/time';
import type { IResourceContentProps } from '../../types';
import { useStyles } from './styles';

export function ResourceAnnouncementContent({
	selected,
}: IResourceContentProps) {
	const { classes } = useStyles();

	const navigate = useNavigate();

	const hasDefinition =
		!isNil(selected.metadata.definition) &&
		!isEmpty(selected.metadata.definition);

	const handleNavigateToUser = () => {
		navigate(`/user/${selected.sender.id}`);
	};
	const handleNavigateToResource = (resource: LiteSecodaEntityOut) => {
		const url = buildResourceUrl(resource);
		navigate(url);
	};

	return (
		<Stack spacing="xl">
			<Stack spacing={0}>
				<Flex gap="xs">
					<UnstyledButton
						onClick={handleNavigateToUser}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
						}}
					>
						{selected.sender ? (
							<UserAvatar user={selected.sender} size="xs" />
						) : null}
						<Text size="sm" color="text/primary/default">
							{selected.sender.display_name}
						</Text>
						<Text size="xs" color="text/secondary/default">
							•
						</Text>
					</UnstyledButton>
					<Text size="sm" color="text/secondary/default">
						{formatDateTimeShort(selected.created_at)}
					</Text>
				</Flex>
				{hasDefinition && (
					<MarkdownRenderer className={classes.removeBorder}>
						{isString(selected.metadata.definition)
							? selected.metadata.definition
							: ''}
					</MarkdownRenderer>
				)}
			</Stack>
			{selected.computedMetadata.relatedResources && (
				<Stack spacing="sm">
					<Text size="sm" color="text/secondary/default">
						Related resources
					</Text>
					{selected.computedMetadata.relatedResources.map((resource) => (
						<Group
							key={resource.id}
							className={classes.relatedResourceGroup}
							onClick={() => handleNavigateToResource(resource)}
						>
							<SecodaEntityIcon entity={resource} size={24} />
							<Stack spacing={0}>
								<Text size="sm" weight="semibold" color="text/primary/default">
									{resource.title}
								</Text>
								<Text size="xs" color="text/secondary/default">
									{getEntityPathString(resource)}
								</Text>
							</Stack>
						</Group>
					))}
				</Stack>
			)}
		</Stack>
	);
}
